import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {Checkbox} from 'native-base';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OrderListCellStyle} from './styles';
import {OrdersForShopper} from '@buncha/batchDetails/types';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Touchable} from '@buncha/components/core/touchable';
import font from '@buncha/appStyles/style/font';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';
import {useNavigateVanLoadContext} from '@buncha/batchDetails/hooks/NavigateVanLoad';

interface Props {
  order: OrdersForShopper;
  checkBoxHandler: (orderId: number, hasChecked: boolean) => void;
  toggleBottomSheet: () => void;
  setBagCountDetails: (order: OrdersForShopper) => void;
}

const OrderListCell = (props: Props) => {
  const {order} = props;
  const assignee = order.orderAssignee;
  const assigneeSecondary = assignee.assigneeSecondary;
  const buyer = order.buyer;
  const muliFont = useFontTheme('muli');
  const bagsUsed = useRef<number>(order.bagsUsed);
  const {body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};

  const theme = useColorThemeType();
  const {state} = useNavigateVanLoadContext();
  const bagCountUpdateStatus = state.bagCountUpdateStatus;

  const isMeijerStore = useIsMeijerStore(state?.run?.masterRun?.store?.id);

  const editBottomSheetHandler = useCallback(() => {
    props?.toggleBottomSheet();
    props?.setBagCountDetails(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  const onChangeHandler = useCallback(() => {
    if (!bagsUsed.current) editBottomSheetHandler();
    props?.checkBoxHandler(order.id, !bagCountUpdateStatus[order.id]);
  }, [bagCountUpdateStatus, editBottomSheetHandler, order.id, props]);

  const getColor = useMemo(() => {
    if (bagCountUpdateStatus[order.id])
      return {
        backgroundColor: theme?.background.green[100],
        borderColor: theme?.text.green[100],
      };
    return {
      backgroundColor: theme?.background.white[100],
      borderColor: theme?.text.blue[300],
    };
  }, [
    bagCountUpdateStatus,
    order.id,
    theme?.background.green,
    theme?.background.white,
    theme?.text.blue,
    theme?.text.green,
  ]);

  const hasDriverAssigned = useMemo(() => {
    if (assigneeSecondary?.firstName) return true;
    return false;
  }, [assigneeSecondary?.firstName]);

  const openIntercom = useCallback(() => {
    Analytics.displayMessenger();
  }, []);

  useEffect(() => {
    bagsUsed.current = order.bagsUsed;
  }, [order.bagsUsed]);

  return (
    <HBox style={OrderListCellStyle.mainContainer}>
      <Box style={OrderListCellStyle.flexBox}>
        <H6Text
          style={[
            OrderListCellStyle.buyerName,
            {
              color: theme?.text.blue[300],
            },
          ]}>
          {buyer?.firstName} {buyer?.lastName}
        </H6Text>
        <If condition={hasDriverAssigned}>
          <CustomText
            style={[
              OrderListCellStyle.driverName,
              {
                color: theme?.text.gray[100],
                fontFamily: font.fonts.muliSemiBold,
              },
            ]}>
            {body?.driver}
            {assigneeSecondary?.firstName} {assigneeSecondary?.lastName}
          </CustomText>
        </If>
        <If condition={!hasDriverAssigned}>
          <CustomText
            style={[
              OrderListCellStyle.driverName,
              {
                color: theme?.text.gray[100],
                fontFamily: font.fonts.muliSemiBold,
              },
            ]}>
            {body?.noDriverAssigned?.[0]}
            <CustomText
              onPress={openIntercom}
              style={[
                OrderListCellStyle.driverName,
                OrderListCellStyle.underLineText,
                {
                  color: theme?.text.blue[100],
                  fontFamily: font.fonts.muliSemiBold,
                },
              ]}>
              {body?.noDriverAssigned?.[1]}
            </CustomText>
          </CustomText>
        </If>
      </Box>
      <HBox>
        <If condition={order?.bagsUsed}>
          <Touchable onPress={editBottomSheetHandler}>
            <CustomText
              style={[
                OrderListCellStyle.underLineText,
                {
                  color: theme?.text.blue[100],
                  ...font.FontSize.fontP10,
                  fontFamily: muliFont[700].normal,
                },
              ]}>
              {order?.bagsUsed ? button?.edit : button?.addBags}
            </CustomText>
          </Touchable>
        </If>
        <If condition={order?.bagsUsed}>
          <Box
            style={[
              OrderListCellStyle.bagBoxContainer,
              {backgroundColor: theme?.background.yellow[100]},
            ]}>
            <CustomText
              style={[
                {
                  color: theme?.text.blue[300],
                  ...font.FontSize.fontP10,
                  fontFamily: font.fonts.muliSemiBold,
                },
              ]}>
              {body?.boxes?.replace('{bagsUsed}', order?.bagsUsed)}
            </CustomText>
          </Box>
        </If>
        <Checkbox
          value={''}
          onChange={onChangeHandler}
          aria-label={`${order?.id}`}
          backgroundColor={getColor.backgroundColor}
          style={[
            OrderListCellStyle.checkBox,
            {borderColor: getColor.borderColor},
          ]}
          isDisabled={!isMeijerStore && !order?.bagsUsed}
          isChecked={bagCountUpdateStatus[order.id] || false}
          defaultIsChecked={bagCountUpdateStatus[order.id] || false}
        />
      </HBox>
    </HBox>
  );
};

export default React.memo(OrderListCell);
